import React from "react";
import { Button } from "react-bootstrap";
import { createRoot } from "react-dom/client";
import { ActionBus } from "../models/ActionBus";

const DialogContextDef = {
	/**
	 * @param {DialogDefType} options
	 * @returns Promise
	 */
	open: async options => { },
};

const DialogContext = React.createContext(DialogContextDef);

export const useDialogContext = () => {
	const context = React.useContext(DialogContext);
	if (!context) {
		throw new Error(
			"useDialogContext must be used within a DialogContextProvider"
		);
	}
	return context;
};

export const DialogContextProvider = ({ children }) => {
	const dialogsContainerRef = React.useRef(undefined);

	/**
	 * @param {DialogDefType} options
	 * @returns Promise
	 */
	const openDialog = async options => {
		const dialogInstance = await makeDialog(options);
		/*
			not showModal() because modal puts the element on the "top-layer"
			and we lose the ability to put other elements that aren't modals (like swal2 confirms) on top
		 */
		dialogInstance.show();
		dialogInstance.addEventListener("close", e => {
			options.actionBus?.send("close");
		}, { once: true });

		if (options.withEvents) {
			options.actionBus ??= new ActionBus();
		}

		options.actionBus?.on("close", () => {
			dialogInstance?.close();
			dialogInstance?.remove();
		});
		options.actionBus?.on("processing", () => {
			dialogInstance.classList.add("processing");
		});
		let preventAutoClose = false;
		options.actionBus?.on("prevent-auto-close", () => {
			preventAutoClose = true;
		});

		const actionResult = new Promise(resolve => {
			dialogInstance.addEventListener("click", e => {
				if (e.target && e.target instanceof HTMLButtonElement && e.target.dataset.actionKey) {
					const actionKey = e.target.dataset.actionKey;
					if (actionKey) {
						const action = options.actions?.find(it => it.key === actionKey);
						action && resolve(action.key);
						if (!preventAutoClose) {
							dialogInstance.close();
							dialogInstance.remove();
						}
						e.preventDefault();
						e.stopPropagation();
					}
				}
			});
		});

		if (options.withInstance || options.withEvents) {
			return {
				instance: dialogInstance,
				result: actionResult,
				events: options.actionBus,
			};
		}
		return actionResult;
	}

	/**
	 * @param {DialogDefType} options
	 * @returns Promise<HTMLDialogElement>
	 */
	const makeDialog = options => {
		const comp = (
			<dialog data-dialog-key={options.key}>
				<h3 className="header">
					{options.heading}
					<button type="button" className="dialog-close-btn" data-action-key="close" aria-label="Close">
						<i className="fa-light fa-times"></i>
					</button>
				</h3>
				<section className="content">
					<DialogContextProvider>
						{options.content}
					</DialogContextProvider>
				</section>
				<footer>
					{((options.close ?? true) || (options.close !== false && options.actions !== null && !options.actions?.find(it => it.key === "cancel" || it.key === "close"))) && (
						<Button type="button" data-action-key="close" variant={options.actions?.length ? 'secondary' : 'outline-primary'}>
							{typeof options.close === "string" ? options.close : "Close"}
						</Button>
					)}
					{options.actions && (
						<section>
							{options.actions?.map(action => (
								<Button variant={action.variant} key={action.key} data-action-key={action.key}>{action.label}</Button>
							))}
						</section>
					)}
				</footer>
			</dialog>
		);

		const wrapper = document.createElement("div");
		const root = createRoot(wrapper);
		root.render(comp);
		dialogsContainerRef.current.append(wrapper)

		// timeout is to allow for rendering, appending, etc before attempting to launch
		return new Promise(resolve => {
			setTimeout(() => {
				resolve(wrapper.firstElementChild)
			}, 150);
		});
	}

	const value = {
		/**
		 * @param {DialogDefType} options
		 * @returns Promise
		 */
		open: openDialog,
	}

	return (
		<DialogContext.Provider value={value} displayName="Dialog Context">
			<div data-component="Dialogs" ref={dialogsContainerRef}></div>

			{children}
		</DialogContext.Provider>
	);
};
